import { IKnmSubject248 } from 'APIPackage/KnmModule/interfaces/IKnmSubject248';
import { deleteObjectProperties } from 'CorePackage/MainModule/common/deleteObjectProperties';
import { getIntNumberByString } from 'CorePackage/MainModule/common/getIntNumberByString';
import { convertFormDateTimeValueToKnmDataDate } from 'KnmPackage/MainModule/common/convertFormDateTimeValueToKnmDataDate';
import { convertKnmDocumentAttachmentsFormValuesToKnmData } from 'KnmPackage/MainModule/common/convertKnmDocumentAttachmentsFormValuesToKnmData';
import { isLaterThanAdminSetting } from 'CorePackage/MainModule/validators/isLaterThanAdminSetting';
import { AdminSettingCodesEnum } from 'APIPackage/AdminModule/enums/AdminSettingCodesEnum';
import { getAdminFetchSettings } from 'PersonalAccountPackage/AdminModule/selectors/adminSelectors';
import { IKnmDictionary } from 'APIPackage/KnmModule/interfaces/IKnmDictionary';
import { store } from 'CorePackage/MainModule/common/store';
import { IKnmActFamiliarizationNotifications } from 'APIPackage/KnmModule/interfaces/IKnmActFamiliarizationNotifications';
import { convertFormDateValueToKnmDataDate } from 'KnmPackage/MainModule/common/convertFormDateValueToKnmDataDate';
import { getDirtyActGuid } from 'CorePackage/HeaderModule/reducers/headerReducer';

import { IKnmErknmFormAct } from '../interfaces/IKnmErknmFormAct';
import { convertInspectorsFormValuesToKnmData } from './convertInspectorsFormValuesToKnmData';
import { convertViolatedRequirementsFormValuesToKnmData } from './convertViolatedRequirementsFormValuesToKnmData';
import { convertEventsFormValuesToKnmData } from './convertEventsFormValuesToKnmData';
import { convertActExpertsFormValueToKnmData } from './convertActExpertsFormValueToKnmData';
import { handleReactQuillDeltaValue } from '../../../functions/handleReactQuillDeltaValue';
import { IKnmErknmForm } from '../interfaces/IKnmErknmForm';
import { convertActRemarksFormToData } from './convertActRemarksFormToData';
import { convertSignersFormToData } from './convertSignersFormToData';

export const convertActFormValuesToKnmData = (
  formAct: IKnmErknmFormAct,
  created: string | undefined,
  values: IKnmErknmForm,
  isDraft: boolean | undefined,
): IKnmSubject248['act'] => {
  const state = store.getState();
  const settings = getAdminFetchSettings(state);
  const dirtyActGuid = getDirtyActGuid(state);

  const isShow4127 = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_27_STARTS_FROM,
  );

  const isShow4122 = isLaterThanAdminSetting(
    created,
    settings,
    AdminSettingCodesEnum.KNM_VALIDATION_4_1_22_STARTS_FROM,
  );

  let fioSigner: string | null;
  let titleSigner: IKnmDictionary | null;

  if (isShow4127 || formAct?.paperless) {
    fioSigner = formAct.fioAndPositionSinger?.value.fullName || null;
    titleSigner = formAct?.fioAndPositionSinger?.value.position?.value || null;
  } else {
    fioSigner = formAct.fioSigner || null;
    titleSigner = formAct?.titleSigner?.value || null;
  }

  return {
    isNew: isDraft ? formAct.isNew : null,
    acquaintanceLackNoInfoComment: formAct.acquaintanceLackNoInfoComment || null,
    acquaintanceLackType: formAct.acquaintanceLackType
      ? deleteObjectProperties(formAct.acquaintanceLackType, ['label', 'value'])
      : null,
    acquaintanceType: formAct.acquaintanceType
      ? deleteObjectProperties(formAct.acquaintanceType, ['label', 'value'])
      : null,
    canceledReason: formAct.canceledReason || null,
    canceledReasonDescription: formAct.canceledReasonDescription || null,
    canceledReasonDescriptionReactQuillEditor: handleReactQuillDeltaValue(
      formAct.canceledReasonDescriptionReactQuillEditor || formAct.canceledReasonDescription || null,
    ),
    created: formAct.created || null,
    createdBy: formAct.createdBy || null,
    creationActDateTime: convertFormDateTimeValueToKnmDataDate(formAct.creationActDateTime),
    signActDateTime: formAct.signActDateTime || null,
    document: !formAct.document
      ? null
      : {
          active: formAct.document?.active || null,
          attachments: convertKnmDocumentAttachmentsFormValuesToKnmData(formAct.document?.attachments),
          correlationToken: formAct.document?.correlationToken || null,
          description: formAct.document?.description || null,
          guid: formAct.document?.guid || null,
          type: formAct.document?.type || null,
        },
    durationDays: getIntNumberByString(formAct?.durationDays),
    durationHours: getIntNumberByString(formAct?.durationHours),
    events: convertEventsFormValuesToKnmData(formAct.events, values.suspensionEvents, undefined, isDraft),
    fioReader: formAct.fioReader || null,
    fioSigner,
    guid: formAct.guid || null,
    holdingDateTime: convertFormDateTimeValueToKnmDataDate(formAct.holdingDateTime),
    isViolationResolved:
      typeof formAct.isViolationResolved?.value === 'boolean' ? formAct.isViolationResolved.value : null,
    isViolationResolvedNote: formAct.isViolationResolvedNote || null,
    isViolations:
      typeof formAct.isViolatedMandatoryRequirements?.value === 'boolean'
        ? formAct.isViolatedMandatoryRequirements.value
        : null,
    knoInspectors: convertInspectorsFormValuesToKnmData(isShow4122, formAct.knoInspectors),
    involvedExperts: convertActExpertsFormValueToKnmData(formAct.involvedExperts),
    involvedWitnesses: !formAct.involvedWitnesses
      ? null
      : formAct.involvedWitnesses.filter(i => i.fullName).map(i => ({ fullName: i.fullName || '', guid: i.listId })),

    nextWordDayActDateTime: formAct.nextWordDayActDateTime || null,
    noteInvalidate: formAct.noteInvalidate || null,
    fileInvalidate: convertKnmDocumentAttachmentsFormValuesToKnmData([formAct.fileInvalidate])?.pop() || null,
    numberAct: formAct.numberAct || null,
    refuserFio: formAct.refuserFio || null,
    refuserTitle: formAct.refuserTitle || null,
    canceled: formAct.canceled || null,
    titleReader: formAct.titleReader || null,
    titleSigner,
    typeAct: formAct.typeAct || null,
    updated: formAct.updated || null,
    updatedBy: formAct.updatedBy || null,
    violations: convertViolatedRequirementsFormValuesToKnmData(formAct.violatedMandatoryRequirements, isDraft),
    familiarizationNotifications:
      formAct?.familiarizationNotifications?.map(x => {
        const notification: IKnmActFamiliarizationNotifications = {
          noticeDate: convertFormDateValueToKnmDataDate(x.noticeDate),
          knmNoticeType: x.knmNoticeType || null,
          note: x.note || null,
          guid: x.listId,
          isNew: x.isNew,
          existedAtSigning: x.existedAtSigning || null,
        };

        return notification;
      }) || null,
    remarks: convertActRemarksFormToData(formAct.remarks),
    signed: dirtyActGuid && dirtyActGuid === formAct.guid ? false : formAct.signed ?? null,
    paperless: formAct.paperless || null,
    extractVersion: formAct.extractVersion || null,
    officialFullNameCancel: formAct.officialFullNameCancel || null,
    resultContentPositionCancel: !formAct.resultContentPositionCancel
      ? null
      : formAct.resultContentPositionCancel.value,
    firstExtractDateTime: convertFormDateTimeValueToKnmDataDate(formAct?.firstExtractDateTime) || null,
    systemSignatureUsed: formAct?.systemSignatureUsed || false,
    systemSignatureTitle: formAct?.systemSignatureTitle || null,
    systemSignatureDateTime: formAct?.systemSignatureDateTime || null,
    systemSignatureMnemonic: formAct?.systemSignatureMnemonic || null,
    signers: convertSignersFormToData(formAct?.signers),
  };
};
